import {EffectRef, Injectable, Signal, WritableSignal, effect, inject, signal} from '@angular/core';
import {Router} from "@angular/router";
import {CustomerService} from "./customer.service";
import {ModuleConfigurationService} from './module-configuration.service';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RouterService {
  private router: Router = inject(Router);
  private customerService: CustomerService = inject(CustomerService);
  private moduleConfigService: ModuleConfigurationService = inject(ModuleConfigurationService);
  private readonly postLoginRedirectStorageKey: string = 'postLoginRedirectUrl';

  private currentTargetSignal: WritableSignal<string> = signal<string>('');
  public readonly currentTarget: Signal<string> = this.currentTargetSignal.asReadonly();
  private triggerStartNavigation: WritableSignal<boolean> = signal<boolean>(false);

  public navigateTo(target: string): void {
    // Check if the target route belongs to a legacy module
    const legacyModule = this.findLegacyModuleByRoute(target);
    
    if (legacyModule) {
      // Navigate to legacy frontend
      const legacyUrl = this.buildLegacyUrl(target);
      this.openURL(legacyUrl);
      console.log('Redirected to legacy frontend: ' + legacyUrl);
      return;
    }

    // Proceed with normal Angular navigation
    const navigationPromise = target.startsWith('/')
      ? this.router.navigateByUrl(target)
      : this.router.navigate([target], {});

    navigationPromise.then(
      () => {
        console.log('Navigated to ' + target);
        this.currentTargetSignal.set(target);
      },
      (error) => {
        console.log('Error navigating to ' + target);
        console.error(error);
      }
    );
  }

  private findLegacyModuleByRoute(target: string): typeof this.moduleConfigService.legacyModules[number] | undefined {
    const normalizedTarget = target.startsWith('/') ? target : '/' + target;
    return this.moduleConfigService.legacyModules.find(module => 
      normalizedTarget.startsWith(module.route)
    );
  }

  private buildLegacyUrl(target: string): string {
    const baseUrl = environment.legacyFrontend.url;
    const normalizedTarget = target.startsWith('/') ? target.substring(1) : target;
    
    // Ensure base URL ends with / and target doesn't start with /
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    return cleanBaseUrl + normalizedTarget;
  }

  public getCurrentUrl(): string {
    return this.router.url;
  }

  public setPostLoginRedirect(target: string | null | undefined): void {
    const sanitizedTarget = this.sanitizePostLoginRedirect(target);
    if (!sanitizedTarget) {
      return;
    }

    try {
      sessionStorage.setItem(this.postLoginRedirectStorageKey, sanitizedTarget);
    } catch (error) {
      console.error('Error storing post login redirect target', error);
    }
  }

  private sanitizePostLoginRedirect(target: string | null | undefined): string | null {
    if (!target || !target.startsWith('/')) {
      return null;
    }

    // Avoid redirecting back into auth routes after login.
    if (target.startsWith('/auth') && target !== '/auth/login/notes') {
      return null;
    }

    return target;
  }

  private consumePostLoginRedirect(): string | null {
    try {
      const target = sessionStorage.getItem(this.postLoginRedirectStorageKey);
      if (target) {
        sessionStorage.removeItem(this.postLoginRedirectStorageKey);
      }
      return target;
    } catch (error) {
      console.error('Error reading post login redirect target', error);
      return null;
    }
  }

  private startNavigationEffect: EffectRef = effect(() => {
    const startPage = this.customerService.customerResource()?.home?.start_page;
    if(this.triggerStartNavigation() && startPage) {
      this.triggerStartNavigation.set(false);
      this.navigateTo(this.moduleConfigService.getRouteForLandingModule(startPage));
    }
  });

  public navigateToStart(): void {
    const postLoginRedirectTarget = this.consumePostLoginRedirect();
    if (postLoginRedirectTarget) {
      this.navigateTo(postLoginRedirectTarget);
      return;
    }

    this.triggerStartNavigation.set(true);
  }

  public openURL(url: string, target = '_blank'): void {
    window.open(url, target);
  }
}
